import { ref, computed } from 'vue';
import { useUsuariosStore } from '@/stores/usuarios';

export const DIAS_PRUEBA = 7;

// Días de prueba TOTALES de un usuario: la base (7) + los días extra que un admin le
// haya sumado a mano desde el panel (campo `pruebaExtraDias`). Si el campo no existe
// (cuentas viejas) o no es un número, cuenta como 0 → se comporta igual que siempre.
export function diasPruebaTotales(usuario) {
  return DIAS_PRUEBA + (Number(usuario?.pruebaExtraDias) || 0);
}

// Días transcurridos desde el registro (número con decimales). Devuelve `null` cuando la
// fecha NO se puede leer (falta o llega en un formato ilegible): eso NO significa "el trial
// venció", significa "no sé". Antes se devolvía Infinity y el código concluía que el trial
// había terminado, mostrándole el cartel de pago a una doctora en pleno período de prueba.
export function diasDesdeRegistroDe(usuario) {
  if (!usuario?.fechaRegistro) return null;
  const fechaRegistro = usuario.fechaRegistro?.toDate
    ? usuario.fechaRegistro.toDate()
    : new Date(usuario.fechaRegistro);
  const ms = fechaRegistro.getTime();
  if (isNaN(ms)) return null;
  return (Date.now() - ms) / (1000 * 60 * 60 * 24);
}

// Días de prueba que le quedan a un usuario (entero, redondeado hacia arriba). Puede dar 0
// o negativo si ya venció; `null` si no se puede determinar la fecha de registro. Sirve para
// mostrar el estado en el panel admin de CUALQUIER usuario (no solo el logueado).
export function diasRestantesPruebaDe(usuario) {
  const dias = diasDesdeRegistroDe(usuario);
  if (dias === null) return null;
  return Math.ceil(diasPruebaTotales(usuario) - dias);
}

export function usePagoVerificacion() {
  const usuariosStore = useUsuariosStore();
  const mostrarModalPago = ref(false);

  // Días transcurridos desde el registro del usuario LOGUEADO (o null si no se puede leer).
  const diasDesdeRegistro = computed(() => diasDesdeRegistroDe(usuariosStore.usuarioActual));

  const enPeriodoPrueba = computed(() => {
    const usuario = usuariosStore.usuarioActual;
    if (!usuario?.pagoPendiente) return false;
    const dias = diasDesdeRegistro.value;
    if (dias === null) return false;
    return dias <= diasPruebaTotales(usuario);
  });

  const diasRestantesPrueba = computed(() => {
    if (!enPeriodoPrueba.value) return 0;
    return Math.ceil(diasPruebaTotales(usuariosStore.usuarioActual) - diasDesdeRegistro.value);
  });

  /**
   * Verifica si el usuario puede realizar modificaciones (crear/editar/eliminar datos).
   * Durante el período de prueba gratuita (7 días + los extra que le hayan dado), todas las
   * funciones están habilitadas.
   * @returns {boolean} true si puede modificar, false si está bloqueado
   */
  function verificarPermisoPago() {
    if (usuariosStore.esAdmin) return true;

    if (usuariosStore.usuarioActual?.pagoPendiente) {
      // Durante la prueba gratuita: todas las funciones habilitadas
      if (enPeriodoPrueba.value) return true;

      // No pudimos determinar la fecha de registro (estado roto, no "trial vencido"):
      // NO bloqueamos. Preferimos dejar trabajar a una doctora antes que mostrarle un
      // paywall por culpa de un dato ilegible. Queda el aviso para poder detectarlo.
      if (diasDesdeRegistro.value === null) {
        console.warn("[pago] No se pudo determinar la fecha de registro; no se bloquea el acceso.");
        return true;
      }

      mostrarModalPago.value = true;
      return false;
    }

    return true;
  }

  function cerrarModalPago() {
    mostrarModalPago.value = false;
  }

  return {
    mostrarModalPago,
    enPeriodoPrueba,
    diasRestantesPrueba,
    verificarPermisoPago,
    cerrarModalPago
  };
}
